import { use, useMemo } from "react";
import { GQL } from "@binale-tech/shared";
import { CompanyAccountingYearContext, CompanyContext } from "@domain/context/CompanyContext";
import { useVatPosition } from "@accounting/common/metrics/useVatPosition";
import { taxDeadlines } from "./taxDeadlines";
import { attachPrepaymentAmounts } from "./taxPrepaymentAmounts";
import { useTaxPrepayments } from "./useTaxPrepayments";
import { type CalendarEvent } from "./types";

export const useTaxDeadlineEvents = (): CalendarEvent[] => {
    const { companyGQL } = use(CompanyContext);
    const { yearConfig } = use(CompanyAccountingYearContext);
    const vat = useVatPosition();
    const prepayments = useTaxPrepayments(companyGQL?.id);

    return useMemo(() => {
        if (!yearConfig) {
            return [];
        }
        const today = new Date();
        const events = taxDeadlines({
            today,
            year: yearConfig.year,
            legalForm: companyGQL?.legalForm,
            taxation: yearConfig.taxation as GQL.ICompanyTaxation,
            fiscalYearStart: yearConfig.fiscalYearStart,
            bundesland: companyGQL?.invoiceTax?.bundesland,
            ustvaFrequency: yearConfig.ustvaFrequency,
            dauerfristverlaengerung: yearConfig.dauerfristverlaengerung,
            steuerlichBeraten: yearConfig.steuerlichBeraten,
        });
        // Attach the current period's Kennzahl-83 amount to the matching UStVA event.
        // For quarterly filers this is a v1 approximation (design spec §II.11 decision 3):
        // `useVatPosition` is monthly-only (no quarter aggregation yet), so the
        // current-month KZ83 is shown on the current QUARTER's row rather than summing
        // all 3 months - documented as a known limitation, not solved here.
        if (vat.status === "ready") {
            const id =
                yearConfig.ustvaFrequency === GQL.IUstvaFilingFrequency.Quarterly
                    ? `ustva-${yearConfig.year}-Q${Math.ceil(vat.period / 3)}`
                    : `ustva-${yearConfig.year}-${String(vat.period).padStart(2, "0")}`;
            const target = events.find((e) => e.id === id);
            if (target) {
                target.amountCents = vat.amount;
            }
        }
        return attachPrepaymentAmounts(events, prepayments, yearConfig.year);
    }, [
        companyGQL?.legalForm,
        companyGQL?.invoiceTax?.bundesland,
        yearConfig,
        vat.status,
        vat.amount,
        vat.period,
        prepayments,
    ]);
};
